import React, { useState, useEffect, useRef } from 'react';
import { executeRecaptcha } from '../services/recaptcha';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from './ui/input';
import { authService } from '../services/authService';
import apiClient from '../services/authService';
import SEOMeta from './SEOMeta';
import {
  Eye, EyeOff,
  CheckCircle2,
  Home, UserRound,
  Loader2,
  Star,
  Check, X,
  HelpCircle,
  Shield, Zap, Users,
} from 'lucide-react';

// ── Left-panel bullets ─────────────────────────────────────────────────────
const BENEFITS = [
  'AI handles tenant maintenance requests 24/7',
  'Lease tracking & rent reminders built in',
  'Set up your first property in under 10 minutes',
];

// ── Role cards ─────────────────────────────────────────────────────────────
const ROLE_OPTIONS = [
  { value: 'Landlord', label: 'Landlord / Property Manager', icon: Home,      sub: 'Manage properties & tenants' },
  { value: 'Tenant',   label: 'Tenant',                      icon: UserRound, sub: 'Access your rental info'     },
];

// ── Trust badges ───────────────────────────────────────────────────────────
const TRUST_BADGES = [
  { icon: Shield, text: 'Bank-level encryption' },
  { icon: Zap,    text: 'Setup in 5 minutes'    },
  { icon: Users,  text: '500+ landlords trust us'},
];

// ── Password strength (4 levels) ──────────────────────────────────────────
const getPasswordStrength = (pw) => {
  if (!pw) return { score: 0, label: '', segments: [false, false, false, false], hint: '' };
  const types = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((r) => r.test(pw)).length;
  if (pw.length < 8)
    return { score: 1, label: 'Weak',   segments: [true, false, false, false], hint: 'Use at least 8 characters' };
  if (pw.length >= 10 && types >= 3)
    return { score: 4, label: 'Strong', segments: [true, true,  true,  true],  hint: 'Great password!' };
  if ((pw.length >= 8 && types >= 2) || (pw.length >= 10 && types >= 3))
    return { score: 3, label: 'Good',   segments: [true, true,  true,  false], hint: 'Add a symbol to strengthen' };
  return   { score: 2, label: 'Fair',   segments: [true, true,  false, false], hint: 'Add uppercase letters or numbers' };
};
const STRENGTH_COLORS = ['', 'bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-emerald-500'];
const STRENGTH_TEXT   = ['', 'text-red-500', 'text-orange-500', 'text-yellow-500', 'text-emerald-600'];

// ── Field validators ───────────────────────────────────────────────────────
const validators = {
  email:           (v)     => !v ? 'Email is required' : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Enter a valid email address' : null,
  password:        (v)     => !v ? 'Password is required' : v.length < 8 ? 'Password must be at least 8 characters' : null,
  confirmPassword: (v, pw) => !v ? 'Please confirm your password' : v !== pw ? "Passwords don't match" : null,
};

// ── Inline field feedback ──────────────────────────────────────────────────
const FieldFeedback = ({ touched, error }) => {
  if (!touched) return null;
  if (error)
    return <p className="flex items-center gap-1 text-xs text-red-500 mt-1"><X className="h-3.5 w-3.5 flex-shrink-0" />{error}</p>;
  return <p className="flex items-center gap-1 text-xs text-emerald-600 mt-1"><Check className="h-3.5 w-3.5 flex-shrink-0" />Looks good</p>;
};

// ── Main component ─────────────────────────────────────────────────────────
const Register = ({ onRegister }) => {
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '',
    userType: 'Landlord', companyName: '', inviteCode: '',
  });
  const [termsAccepted,       setTermsAccepted]       = useState(false);
  const [error,               setError]               = useState('');
  const [loading,             setLoading]             = useState(false);
  const [googleLoading,       setGoogleLoading]       = useState(false);
  const [accountTypeStep,     setAccountTypeStep]     = useState(null); // null | 'choose' | 'invite'
  const [socialInviteCode,    setSocialInviteCode]    = useState('');
  const [modalError,          setModalError]          = useState('');
  const [showPassword,        setShowPassword]        = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched,             setTouched]             = useState({ email: false, password: false, confirmPassword: false });
  const [showTooltip,         setShowTooltip]         = useState(false);
  const [inviteFromUrl,       setInviteFromUrl]       = useState(false);


  // Success animation state
  const [showSuccess,   setShowSuccess]   = useState(false);
  const [successVisible, setSuccessVisible] = useState(false); // drives CSS scale-in
  // Stores the navigation callback so it can fire after delay
  const pendingNav = useRef(null);

  const navigate    = useNavigate();
  const tooltipRef  = useRef(null);
  const [searchParams] = useSearchParams();
  const isInviteFlow = Boolean(formData.inviteCode);

  // ── Seed from URL invite params ──────────────────────────────────────────
  // Supports both ?inviteCode= (tenant portal) and ?code= (application invite)
  useEffect(() => {
    const inviteCode  = searchParams.get('inviteCode') || searchParams.get('code');
    const inviteEmail = searchParams.get('email');
    if (inviteCode) {
      setInviteFromUrl(true);
      setFormData((prev) => ({ ...prev, inviteCode, userType: 'Tenant', email: inviteEmail || prev.email }));
    }
  }, [searchParams]);

  // ── Close tooltip on outside click ──────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) setShowTooltip(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Trigger success scale-in and navigate after 1.5 s ───────────────────
  useEffect(() => {
    if (!showSuccess) return;
    // one frame delay so the transition fires after mount
    const rafId = requestAnimationFrame(() => setSuccessVisible(true));
    const timerId = setTimeout(() => {
      pendingNav.current?.();
    }, 1500);
    return () => { cancelAnimationFrame(rafId); clearTimeout(timerId); };
  }, [showSuccess]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };
  const handleBlur = (e) => setTouched((prev) => ({ ...prev, [e.target.name]: true }));

  // ── Derived state ────────────────────────────────────────────────────────
  const isTenant = formData.userType === 'Tenant' || isInviteFlow;
  const fieldErrors = {
    email:           validators.email(formData.email),
    password:        validators.password(formData.password),
    confirmPassword: validators.confirmPassword(formData.confirmPassword, formData.password),
    inviteCode:      isTenant && !formData.inviteCode ? 'Invitation code is required for tenants' : null,
  };
  const isFormValid = !fieldErrors.email && !fieldErrors.password && !fieldErrors.confirmPassword && !fieldErrors.inviteCode && termsAccepted;
  const strength    = getPasswordStrength(formData.password);
  const showCompany = formData.userType === 'Landlord' && !isInviteFlow;

  const fieldRing = (name) =>
    touched[name] && fieldErrors[name]   ? 'border-red-400 focus-visible:ring-red-300' :
    touched[name] && !fieldErrors[name]  ? 'border-emerald-400 focus-visible:ring-emerald-300' : '';

  // ── Google sign-in ───────────────────────────────────────────────────────
  const handleModalClose = async () => {
    setAccountTypeStep(null);
    setSocialInviteCode('');
    setModalError('');
    await authService.logout();
  };

  const handleAccountTypeConfirm = async (userType) => {
    setGoogleLoading(true);
    setModalError('');
    try {
      const result = await authService.completeSocialLogin({
        userType,
        inviteCode: userType === 'Tenant' ? socialInviteCode.trim() : null,
      });
      setAccountTypeStep(null);
      onRegister(result.user);
      navigate('/niva');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    setAccountTypeStep(null);
    setSocialInviteCode('');
    try {
      // URL invite flow → pass the code directly; no modal needed
      // All other cases → let the modal ask for role (avoids silently creating wrong type)
      const result = await authService.signInWithGoogle(
        isInviteFlow ? { inviteCode: formData.inviteCode } : {}
      );
      if (result.needsAccountType) {
        setAccountTypeStep('choose');
        return;
      }
      onRegister(result.user);
      navigate('/niva');
    } catch (err) {
      if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled'].includes(err.code)) return;
      setError(err.response?.data?.error || err.message || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ email: true, password: true, confirmPassword: true });
    if (!isFormValid) return;

    setLoading(true);
    setError('');
    try {
      const recaptchaToken = await executeRecaptcha('register');
      const registerData = {
        email:          formData.email,
        password:       formData.password,
        userType:       isInviteFlow ? 'Tenant' : formData.userType,
        recaptchaToken,
      };
      if (formData.inviteCode) registerData.inviteCode = formData.inviteCode;
      if (!isInviteFlow && formData.userType === 'Landlord' && formData.companyName)
        registerData.companyName = formData.companyName;

      const response = await authService.register(registerData);

      // Set up the pending navigation, then show success card
      if (response.requiresVerification) {
        // After email verification the user lands on /login — pass flag so banner shows
        pendingNav.current = () => navigate('/verify-email', { state: { email: registerData.email, justRegistered: true } });
      } else if (response.isApplicantFlow) {
        // Applicant flow: store token, call init to link invite → user, then go to the apply chat
        localStorage.setItem('token', response.token);
        onRegister(response.user);
        try {
          await apiClient.post('/api/applications/init', { inviteCode: registerData.inviteCode });
        } catch (initErr) {
          console.warn('[Register] /api/applications/init failed:', initErr?.response?.data);
        }
        pendingNav.current = () => navigate('/apply/chat');
      } else {
        localStorage.setItem('token', response.token);
        onRegister(response.user);
        pendingNav.current = () => navigate('/');
      }
      setShowSuccess(true);

    } catch (err) {
      setError(
        err.response?.status === 403
          ? (err.response.data.message || 'Registration is currently closed. Please join our waitlist for early access.')
          : (err.response?.data?.error || 'Registration failed. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>

    {/* ── Account-type modal — appears after Google popup for brand-new users ── */}
    {accountTypeStep && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">

          {/* Modal header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-8 w-8" />
              <span className="text-sm font-bold text-gray-900">NivasaPro</span>
            </div>
            <button
              type="button"
              onClick={handleModalClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Step 1 — choose role */}
          {accountTypeStep === 'choose' && (
            <div className="px-6 py-6">
              <h3 className="text-xl font-bold text-gray-900 mb-1">How are you joining?</h3>
              <p className="text-sm text-gray-500 mb-6">Choose the account type that fits you. We'll set everything up.</p>

              {modalError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  <X className="h-4 w-4 flex-shrink-0" />{modalError}
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleAccountTypeConfirm('Landlord')}
                  disabled={googleLoading}
                  className="w-full flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 p-4 text-left transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <div className="h-11 w-11 rounded-xl bg-cyan-100 group-hover:bg-cyan-200 flex items-center justify-center flex-shrink-0 transition-colors">
                    <Home className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">Landlord / Property Manager</p>
                    <p className="text-xs text-gray-500 mt-0.5">Manage properties, tenants, and maintenance</p>
                  </div>
                  {googleLoading
                    ? <Loader2 className="h-4 w-4 animate-spin text-gray-400 flex-shrink-0" />
                    : <CheckCircle2 className="h-4 w-4 text-gray-200 group-hover:text-cyan-400 flex-shrink-0 transition-colors" />
                  }
                </button>

                <button
                  type="button"
                  onClick={() => { setModalError(''); setAccountTypeStep('invite'); }}
                  disabled={googleLoading}
                  className="w-full flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 p-4 text-left transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <div className="h-11 w-11 rounded-xl bg-cyan-100 group-hover:bg-cyan-200 flex items-center justify-center flex-shrink-0 transition-colors">
                    <UserRound className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">Tenant</p>
                    <p className="text-xs text-gray-500 mt-0.5">Access your rental info with an invite code</p>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-gray-200 group-hover:text-cyan-400 flex-shrink-0 transition-colors" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — tenant invite code */}
          {accountTypeStep === 'invite' && (
            <div className="px-6 py-6">
              <button
                type="button"
                onClick={() => { setModalError(''); setAccountTypeStep('choose'); }}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
              >
                ← Back
              </button>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Enter your invite code</h3>
              <p className="text-sm text-gray-500 mb-5">Your landlord included this in their invitation email.</p>

              {modalError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  <X className="h-4 w-4 flex-shrink-0" />{modalError}
                </div>
              )}

              <input
                type="text"
                value={socialInviteCode}
                onChange={e => { setSocialInviteCode(e.target.value.toUpperCase()); setModalError(''); }}
                placeholder="e.g. ABC123"
                className="w-full h-12 px-4 rounded-xl border-2 border-gray-200 focus:border-cyan-500 focus:outline-none text-sm font-mono tracking-widest mb-4 transition-colors"
                autoFocus
              />
              <button
                type="button"
                onClick={() => handleAccountTypeConfirm('Tenant')}
                disabled={!socialInviteCode.trim() || googleLoading}
                className="w-full h-11 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {googleLoading ? <><Loader2 className="h-4 w-4 animate-spin" />Verifying…</> : 'Continue as Tenant'}
              </button>
            </div>
          )}

        </div>
      </div>
    )}

    <div className="min-h-screen flex">
      <SEOMeta title="Create Account" description="Create your NivasaPro account and start managing properties with AI." canonical="/register" noIndex />

      {/* ════════════════════════════════════════════════════════════════
          LEFT PANEL  (hidden on mobile)
      ════════════════════════════════════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-2/5 flex-col relative overflow-hidden" style={{ backgroundColor: '#0F4C5C' }}>
        {/* Gradient mesh blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ backgroundColor: '#22D3EE' }} />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-15 blur-3xl"  style={{ backgroundColor: '#0EA5E9' }} />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full opacity-10 blur-2xl"  style={{ backgroundColor: '#6EE7F7' }} />
        </div>

        <div className="relative z-10 flex flex-col h-full px-10 py-10">
          {/* Logo + Home link */}
          <div className="flex items-center justify-between mb-14">
            <div className="flex items-center gap-2.5">
              <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-9 w-9" />
              <div>
                <span className="text-xl font-bold text-white tracking-tight block leading-none">NivasaPro</span>
                <span className="text-xs text-white/50 leading-none">Agentic AI property management</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-xs text-white/50 hover:text-white/90 transition-colors flex items-center gap-1"
            >
              ← Home
            </button>
          </div>

          <div className="flex-1">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Property management<br />on autopilot.
            </h1>
            <p className="text-white/60 text-sm leading-relaxed mb-10">
              Let AI handle the day-to-day so you can focus on growing your portfolio.
            </p>

            <ul className="space-y-4 mb-12">
              {BENEFITS.map((benefit) => (
                <li key={benefit} className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full bg-white/15 flex items-center justify-center">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300" />
                  </span>
                  <span className="text-white/85 text-sm leading-snug">{benefit}</span>
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-3 gap-2 mb-10 opacity-25">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="h-12 rounded-lg border border-white/30"
                  style={{ backgroundColor: i % 3 === 0 ? 'rgba(255,255,255,0.08)' : 'transparent' }} />
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="border border-white/15 rounded-xl p-5 bg-white/5 backdrop-blur-sm">
            <div className="flex gap-0.5 mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}
            </div>
            <p className="text-white/80 text-sm leading-relaxed italic mb-3">
              "Niva sorted our emergency maintenance call at midnight and had a plumber
              confirmed before I even saw the notification. Incredible."
            </p>
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">RK</div>
              <div>
                <p className="text-white text-xs font-semibold">Rajiv K.</p>
                <p className="text-white/50 text-xs">Portfolio landlord · 14 units</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          RIGHT PANEL
      ════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 lg:w-3/5 flex flex-col bg-white overflow-y-auto">

        {/* ── Mobile header bar (replaces left panel on small screens) ── */}
        <div className="lg:hidden flex items-center justify-between px-5 py-3 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-7 w-7" />
            <div>
              <span className="text-sm font-bold text-gray-900 leading-none block">NivasaPro</span>
              <span className="text-[11px] text-gray-400 leading-none">Agentic AI property management</span>
            </div>
          </div>
          <button onClick={() => navigate('/')} className="text-xs text-gray-500 hover:text-cyan-600 transition-colors">
            ← Home
          </button>
        </div>

        <div className="flex-1 flex items-start justify-center px-5 py-8 sm:px-8 lg:px-16 xl:px-24">
          <div className="w-full max-w-md">

            {/* ── SUCCESS CARD (replaces form after submit) ──────────── */}
            {showSuccess ? (
              <div className="flex flex-col items-center justify-center py-16 text-center gap-5">
                <div
                  className="transition-all duration-500 ease-out"
                  style={{ transform: successVisible ? 'scale(1)' : 'scale(0.4)', opacity: successVisible ? 1 : 0 }}
                >
                  <div className="h-24 w-24 rounded-full bg-emerald-100 flex items-center justify-center shadow-lg">
                    <CheckCircle2 className="h-14 w-14 text-emerald-500" />
                  </div>
                </div>
                <div
                  className="transition-all duration-500 delay-200 ease-out"
                  style={{ opacity: successVisible ? 1 : 0, transform: successVisible ? 'translateY(0)' : 'translateY(8px)' }}
                >
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Welcome to NivasaPro!</h3>
                  <p className="text-sm text-gray-500">Redirecting to your dashboard…</p>
                </div>
                <Loader2
                  className="h-4 w-4 animate-spin text-gray-300 transition-opacity duration-500 delay-300"
                  style={{ opacity: successVisible ? 1 : 0 }}
                />
              </div>
            ) : (
              <>
                {/* ── 1. TRUST BADGE ROW ─────────────────────────────── */}
                <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                  {TRUST_BADGES.map(({ icon: Icon, text }) => (
                    <span key={text} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-[11px] font-medium text-gray-500">
                      <Icon className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      {text}
                    </span>
                  ))}
                </div>

                {/* ── Heading ────────────────────────────────────────── */}
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Create your account</h2>
                  <p className="text-sm text-gray-500">Your AI property manager starts on day one.</p>
                </div>

                {/* ── 2. INVITE BANNER (URL invite) ──────────────────── */}
                {inviteFromUrl && (
                  <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                    <span className="text-lg leading-none mt-0.5 flex-shrink-0">🎉</span>
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">You've been invited to apply!</p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        Create your account to start your rental application. We'll take you straight there.
                      </p>
                    </div>
                  </div>
                )}

                {/* ── Server error banner ────────────────────────────── */}
                {error && (
                  <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <X className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>{error}</span>
                  </div>
                )}

                {/* ── Google sign-in ───────────────────────────────── */}
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading || loading}
                  className="w-full h-11 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.98] text-sm font-medium text-gray-700 flex items-center justify-center gap-3 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed mb-5"
                >
                  {googleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0" aria-hidden="true">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </button>

                <div className="relative mb-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-3 text-gray-400">or sign up with email</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" noValidate>

                  {/* ── Role toggle cards ──────────────────────────── */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">I am a</label>
                    {isInviteFlow ? (
                      <p className="text-xs text-gray-500 bg-gray-50 border rounded-lg px-3 py-2">
                        Invitation detected. Your account will be set up automatically.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {ROLE_OPTIONS.map(({ value, label, icon: Icon, sub }) => {
                          const selected = formData.userType === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              disabled={loading}
                              onClick={() => { setFormData((prev) => ({ ...prev, userType: value })); if (error) setError(''); }}
                              className={[
                                'relative flex flex-col items-start gap-1 rounded-xl border-2 px-4 py-3.5 text-left',
                                'transition-all duration-200 ease-in-out',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500',
                                selected  ? 'border-cyan-500 bg-cyan-50 shadow-sm scale-[1.02]'
                                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
                                loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                              ].join(' ')}
                            >
                              {selected && (
                                <span className="absolute top-2.5 right-2.5 h-4 w-4 rounded-full bg-cyan-500 flex items-center justify-center">
                                  <Check className="h-2.5 w-2.5 text-white" />
                                </span>
                              )}
                              <div className={`h-8 w-8 rounded-lg flex items-center justify-center mb-0.5 transition-colors duration-200 ${selected ? 'bg-cyan-100' : 'bg-gray-100'}`}>
                                <Icon className={`h-4 w-4 transition-colors duration-200 ${selected ? 'text-cyan-600' : 'text-gray-500'}`} />
                              </div>
                              <span className={`text-xs font-semibold leading-tight transition-colors duration-200 ${selected ? 'text-cyan-700' : 'text-gray-800'}`}>{label}</span>
                              <span className="text-[11px] text-gray-400 leading-tight">{sub}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* ── Company name — animated collapse ─────────────── */}
                  <div style={{ maxHeight: showCompany ? '120px' : '0px', opacity: showCompany ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.25s ease, opacity 0.2s ease' }}>
                    <div className="space-y-1.5 pt-0.5">
                      <label className="text-sm font-medium text-gray-700" htmlFor="companyName">
                        Company Name <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <Input id="companyName" name="companyName" value={formData.companyName} onChange={handleChange}
                        disabled={loading || !showCompany} placeholder="e.g., ABC Property Management"
                        className="h-10" tabIndex={showCompany ? 0 : -1} />
                    </div>
                  </div>

                  {/* ── Invitation code + tooltip ─────────────────────── */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <label className="text-sm font-medium text-gray-700" htmlFor="inviteCode">
                        Invitation Code{' '}
                        {isTenant
                          ? <span className="text-red-500 font-normal">(required)</span>
                          : <span className="text-gray-400 font-normal">(if provided)</span>}
                      </label>
                      <div className="relative" ref={tooltipRef}>
                        <button type="button" aria-label="What is an invitation code?"
                          onClick={() => setShowTooltip((v) => !v)}
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                          className="text-gray-400 hover:text-cyan-500 transition-colors focus:outline-none">
                          <HelpCircle className="h-3.5 w-3.5" />
                        </button>
                        <div role="tooltip" className={['absolute left-0 top-6 z-20 w-64 rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-lg text-xs text-gray-600 leading-relaxed', 'transition-all duration-150 origin-top-left', showTooltip ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'].join(' ')}>
                          Enter the invite code from your landlord's email. This links your account and takes you straight to your rental application.
                        </div>
                      </div>
                    </div>
                    <Input id="inviteCode" name="inviteCode" value={formData.inviteCode} onChange={handleChange}
                      disabled={loading || isInviteFlow} placeholder="Enter invitation code" className="h-10" />
                    {formData.inviteCode && isInviteFlow
                      ? <p className="flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3.5 w-3.5" />Pre-filled from your invite link</p>
                      : isTenant && !formData.inviteCode && <p className="text-xs text-red-500">Contact your landlord for an invitation code.</p>
                    }
                  </div>

                  {/* ── Email ─────────────────────────────────────────── */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700" htmlFor="email">Email address</label>
                    <Input id="email" name="email" type="email" placeholder="you@example.com"
                      value={formData.email} onChange={handleChange} onBlur={handleBlur}
                      required disabled={loading} className={`h-10 transition-colors duration-150 ${fieldRing('email')}`} />
                    <FieldFeedback touched={touched.email} error={fieldErrors.email} />
                  </div>

                  {/* ── Password + strength meter ─────────────────────── */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700" htmlFor="password">Password</label>
                    <div className="relative">
                      <Input id="password" name="password" type={showPassword ? 'text' : 'password'}
                        value={formData.password} onChange={handleChange} onBlur={handleBlur}
                        required disabled={loading} minLength={8} placeholder="Min. 8 characters"
                        className={`h-10 pr-10 transition-colors duration-150 ${fieldRing('password')}`} />
                      <button type="button" onClick={() => setShowPassword((v) => !v)} tabIndex={-1}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {formData.password && (
                      <div className="pt-1 space-y-1.5">
                        <div className="flex gap-1" role="meter" aria-label={`Password strength: ${strength.label}`}>
                          {strength.segments.map((active, i) => (
                            <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${active ? STRENGTH_COLORS[strength.score] : 'bg-gray-200'}`} />
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-semibold ${STRENGTH_TEXT[strength.score]}`}>{strength.label}</span>
                          {strength.score < 4
                            ? <span className="text-[11px] text-gray-400">{strength.hint}</span>
                            : <span className="text-[11px] text-emerald-500 flex items-center gap-0.5"><Check className="h-3 w-3" />{strength.hint}</span>
                          }
                        </div>
                      </div>
                    )}
                    {touched.password && !formData.password && <FieldFeedback touched={touched.password} error={fieldErrors.password} />}
                  </div>

                  {/* ── Confirm password ──────────────────────────────── */}
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700" htmlFor="confirmPassword">Confirm Password</label>
                    <div className="relative">
                      <Input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword} onChange={handleChange} onBlur={handleBlur}
                        required disabled={loading} placeholder="Re-enter your password"
                        className={`h-10 pr-10 transition-colors duration-150 ${fieldRing('confirmPassword')}`} />
                      <button type="button" onClick={() => setShowConfirmPassword((v) => !v)} tabIndex={-1}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600">
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {/* Real-time match check — no blur needed */}
                    {formData.confirmPassword
                      ? <FieldFeedback touched={true} error={fieldErrors.confirmPassword} />
                      : <FieldFeedback touched={touched.confirmPassword} error={fieldErrors.confirmPassword} />
                    }
                  </div>

                  {/* ── 3. UPDATED T&C COPY ───────────────────────────── */}
                  <div className="flex items-start gap-3 pt-1">
                    <input id="termsAccepted" type="checkbox" checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)} disabled={loading}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer shrink-0" />
                    <label htmlFor="termsAccepted" className="text-xs text-gray-500 cursor-pointer leading-relaxed">
                      I agree to the{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline font-medium">
                        Terms of Service
                      </a>{' '}
                      and understand that Niva, the AI agent, may send SMS communications on my behalf
                      to tenants I manage.
                    </label>
                  </div>

                  {/* ── Submit button ─────────────────────────────────── */}
                  <button type="submit" disabled={loading || !isFormValid}
                    title={!termsAccepted ? 'Please accept the Terms of Service to continue' : !isFormValid ? 'Please fill in all required fields correctly' : undefined}
                    className={['w-full h-11 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all duration-200',
                      loading || !isFormValid ? 'bg-cyan-300 cursor-not-allowed' : 'bg-cyan-600 hover:bg-cyan-700 active:scale-[0.98] shadow-sm hover:shadow-md',
                    ].join(' ')}>
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" />Creating your account…</> : 'Create Account'}
                  </button>

                </form>

                <p className="mt-6 text-center text-sm text-gray-500">
                  Already have an account?{' '}
                  <RouterLink to="/login" className="text-cyan-600 font-medium hover:underline">Login here</RouterLink>
                </p>
              </>
            )}

          </div>
        </div>
      </div>
    </div>

    </>
  );
};

export default Register;
